// ارزیابی پوست (US-CLN-110؛ BR-CLN-110): فیتزپاتریک الزامی پیش از جلسه لیزر؛ نوع پوست، رنگ و ضخامت مو اختیاری. ثبت افزایشی (append-only).
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { unwrap, type ClnFitzpatrickType, type ClnHairColor, type ClnHairThickness, type ClnSkinType } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { clnKeys } from './common';
import { FITZPATRICK, HAIR_COLORS, HAIR_THICKNESS, SKIN_TYPES } from './beautyCommon';

export interface SkinAssessmentDialogProps {
  patientId: string;
  encounterId?: string | null;
  current?: ClnFitzpatrickType | null;
  onClose: () => void;
  onSaved?: () => void;
}

export function SkinAssessmentDialog({ patientId, encounterId, current, onClose, onSaved }: SkinAssessmentDialogProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [fitzpatrick, setFitzpatrick] = useState<string>(current ?? '');
  const [skinType, setSkinType] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [hairThickness, setHairThickness] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/skin-assessments', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': key.current() },
          body: {
            fitzpatrick: fitzpatrick as ClnFitzpatrickType,
            skinType: (skinType || null) as ClnSkinType,
            hairColor: (hairColor || null) as ClnHairColor,
            hairThickness: (hairThickness || null) as ClnHairThickness,
            note: note.trim() || null,
            encounterId: encounterId ?? null,
          },
        }),
      ),
    onSettled: (_data, error) => key.settle(error),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clnKeys.patient(session?.tenantId, patientId) });
      void queryClient.invalidateQueries({ queryKey: ['clinical', session?.tenantId, 'encounter'] });
      onSaved?.();
      onClose();
    },
  });
  const submit = () => {
    setTouched(true);
    if (fitzpatrick) {
      save.mutate();
    }
  };
  const select = (label: string, value: string, onChange: (value: string) => void, options: readonly string[], prefix: string, testId: string, required = false) => (
    <TextField
      select
      fullWidth
      required={required}
      label={label}
      value={value}
      error={required && touched && !value}
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ htmlInput: { 'data-testid': testId } }}
    >
      {!required && <MenuItem value="">—</MenuItem>}
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {t(`cln:${prefix}.${option}`)}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <FormDialog open titleKey="cln:skin.title" onSubmit={submit} onClose={onClose} isDirty={Boolean(fitzpatrick || skinType || note)} isSubmitting={save.isPending} submitError={save.error}>
      {select(t('cln:skin.fitzpatrick'), fitzpatrick, setFitzpatrick, FITZPATRICK, 'fitzpatrick', 'cln-skin-fitzpatrick', true)}
      {select(t('cln:skin.skin_type'), skinType, setSkinType, SKIN_TYPES, 'skin_type', 'cln-skin-type')}
      {select(t('cln:skin.hair_color'), hairColor, setHairColor, HAIR_COLORS, 'hair_color', 'cln-skin-hair-color')}
      {select(t('cln:skin.hair_thickness'), hairThickness, setHairThickness, HAIR_THICKNESS, 'hair_thickness', 'cln-skin-hair-thickness')}
      <TextField fullWidth multiline minRows={2} label={t('cln:skin.note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000 } }} />
    </FormDialog>
  );
}
